import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import SectionHeader from '@/components/Common/SectionHeader';
import DisplayImage from '../display-image';
import {borderRadius, colors, fontSize, fontWeight, shadows} from '@/styles/tokens';

interface MvpWhyChooseCard {
    title: string;
    description: string;
    image: string | StaticImageData;
    span: number;
}

interface MvpWhyChooseProps {
    heading: string;
    subheading: string;
    cards: MvpWhyChooseCard[];
}

const gridItemSx = {display: 'flex'} as const;

const IMAGE_HOVER_ZOOM = 1.06;

const cardSx = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    bgcolor: colors.background.default,
    border: `1px solid ${colors.neutral.borderLight}`,
    borderRadius: borderRadius.xl,
    boxShadow: shadows.card,
    p: {xs: 1.25, md: 1.5},
    // The site's standard white-card hover — same lift, shadow and timing as
    // `About-us/coreServices.tsx`, which uses the identical card shell.
    transition: 'transform 0.25s ease, box-shadow 0.25s ease',
    '&:hover': {transform: 'translateY(-6px)', boxShadow: shadows.lg},
    // On top of the lift, the photo zooms in slightly inside its clipped frame. Only the
    // image scales — the text never does, so it stays sharp.
    '& img': {transition: 'transform 0.5s ease'},
    '&:hover img': {transform: `scale(${IMAGE_HOVER_ZOOM})`},
    '@media (prefers-reduced-motion: reduce)': {'&:hover img': {transform: 'none'}}
} as const;

// The ratio scales with the card's column span so a wide card's image renders at the
// same height as the narrow ones — a fixed 16:9 made the span-8 card twice as tall.
const imageWrapSx = (span: number) =>
    ({
        position: 'relative',
        width: '100%',
        aspectRatio: {xs: '16 / 9', md: `${(16 * span) / 4} / 9`},
        borderRadius: borderRadius.lg,
        overflow: 'hidden',
        mb: 2
    } as const);

// Without this the shared default claims 33vw, so the browser picks a too-small
// srcset candidate for the span-8 card and it renders softer than the narrow ones.
const imageSizes = (span: number) => `(max-width: 600px) 100vw, (max-width: 900px) 50vw, ${Math.round((span / 12) * 100)}vw`;

const bodySx = {px: {xs: 0.75, md: 1}, pb: {xs: 0.75, md: 1}} as const;

// The heading is long, so the header block drops SectionHeader's 64rem cap and spans the
// full container. The cap is responsive (`{xs, md}`), so the override has to be too.
const headerWrapSx = {maxWidth: {xs: 'none', md: 'none'}} as const;
const headerSubheadingSx = {color: colors.text.primary} as const;

const titleSx = {color: colors.text.primary, fontWeight: fontWeight.bold, fontSize: fontSize[18], lineHeight: 1.4, mb: 1.5} as const;

const descSx = {color: colors.text.primary, fontSize: fontSize[14], lineHeight: 1.8} as const;

const renderCard = (card: MvpWhyChooseCard, md: number) => (
    <Grid item xs={12} sm={6} md={md} key={card.title} sx={gridItemSx}>
        <Box sx={cardSx}>
            <Box sx={imageWrapSx(card.span)}>
                <DisplayImage src={card.image} alt={card.title} fill sizes={imageSizes(card.span)} style={{objectFit: 'cover'}} />
            </Box>
            <Box sx={bodySx}>
                <Typography component="h3" className="font-headers" sx={titleSx}>
                    {card.title}
                </Typography>
                <Typography component="p" className="font-regular-text" sx={descSx}>
                    {parse(card.description)}
                </Typography>
            </Box>
        </Box>
    </Grid>
);

const MvpWhyChoose = ({heading, subheading, cards}: MvpWhyChooseProps) => {
    return (
        <Box>
            <MUIContainer>
                <SectionHeader
                    heading={heading}
                    subheading={parse(subheading)}
                    wrapperSx={headerWrapSx}
                    subheadingSx={headerSubheadingSx}
                />

                <Grid container spacing={3}>
                    {cards?.map((card) => renderCard(card, card.span))}
                </Grid>
            </MUIContainer>
        </Box>
    );
};

export default memo(MvpWhyChoose);
